import { Injectable, inject } from '@angular/core';
import { HTTP_Pagination_Response } from '@api-new/common';
import {
  HTTP_CP_MatchMortgageProducts_Response,
  HTTP_CP_MatchMortgageProducts_Response_MortgageProduct,
  MortgageApiService,
} from '@api-new/mortgageservice';
import { ComponentStore } from '@ngrx/component-store';
import { tapResponse } from '@ngrx/operators';
import { Observable, filter } from 'rxjs';
import { switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { MortgagePartFormValues } from '../../../mortgage-part.form';
import { MortgageFormProductMatchingInfoModel } from '../../models/mortgage-form-product-matching-info.model';
import { productMatchingInfoToRequestAdapter } from './adapters/product-matching-info-to-request.adapter';

interface ProductMatchItemListState {
  productMatchingInfo: MortgageFormProductMatchingInfoModel | null;
  mortgagePart: MortgagePartFormValues | null;
  products: HTTP_CP_MatchMortgageProducts_Response_MortgageProduct[];
  pagination: HTTP_Pagination_Response;
  loadingActions: {
    isLoadingProducts: boolean;
    isLoadingMoreProducts: boolean;
  };
}

const initialProductMatchItemListState: ProductMatchItemListState = {
  productMatchingInfo: null,
  mortgagePart: null,
  products: [],
  pagination: {
    pageNumber: 0,
    pageSize: 5,
    totalCount: 0,
    totalPages: 0,
  },
  loadingActions: {
    isLoadingProducts: false,
    isLoadingMoreProducts: false,
  },
};

@Injectable()
export class ProductMatchItemListStore extends ComponentStore<ProductMatchItemListState> {
  private readonly mortgageApiService = inject(MortgageApiService);

  constructor() {
    super(initialProductMatchItemListState);
  }

  // SELECTORS ---------------------------------------------------------------------------------------------------------
  readonly isLoadingProducts$ = this.select((state) => state.loadingActions.isLoadingProducts);
  readonly isLoadingMoreProducts$ = this.select((state) => state.loadingActions.isLoadingMoreProducts);
  readonly products$ = this.select((state) => state.products);
  readonly mortgagePartInfo$ = this.select((state) => ({
    productMatchingInfo: state.productMatchingInfo,
    mortgagePart: state.mortgagePart,
  }));

  readonly isLastPage$ = this.select((state) => state.pagination.pageNumber >= state.pagination.totalPages);

  // EFFECTS -----------------------------------------------------------------------------------------------------------
  readonly getProductsForMatching = this.effect(
    (
      getProductsForMatching$: Observable<{
        productMatchingInfo: MortgageFormProductMatchingInfoModel;
        mortgagePart?: MortgagePartFormValues;
      }>,
    ) =>
      getProductsForMatching$.pipe(
        tap(({ productMatchingInfo, mortgagePart }) => {
          this.setIsLoadingProducts(true);
          this.setMortgagePartInfo({ productMatchingInfo, mortgagePart });
        }),
        switchMap(({ mortgagePart, productMatchingInfo }) => {
          return this.mortgageApiService
            .HTTP_CP_MatchMortgageProducts(
              productMatchingInfoToRequestAdapter(productMatchingInfo, mortgagePart ?? null, { pageNumber: 1, pageSize: 5 }),
            )
            .pipe(
              tapResponse({
                next: (response: HTTP_CP_MatchMortgageProducts_Response) => {
                  this.setProducts(response);
                },
                error: () => this.setIsLoadingProducts(false),
              }),
            );
        }),
      ),
  );

  readonly loadMoreProducts = this.effect((loadMoreProducts$: Observable<void>) =>
    loadMoreProducts$.pipe(
      tap(() => {
        this.incrementPagination();
        this.setIsLoadingMoreProducts(true);
      }),
      withLatestFrom(this.select((state) => state)),
      filter(([_, state]) => state.productMatchingInfo != null),
      switchMap(([_, { productMatchingInfo, mortgagePart, pagination }]) =>
        this.mortgageApiService
          .HTTP_CP_MatchMortgageProducts(productMatchingInfoToRequestAdapter(productMatchingInfo!, mortgagePart, pagination))
          .pipe(
            tapResponse({
              next: (response: HTTP_CP_MatchMortgageProducts_Response) => {
                this.setProducts({
                  mortgageProducts: [...this.get().products, ...response.mortgageProducts],
                  pagination: response.pagination,
                });
                this.setIsLoadingMoreProducts(false);
              },
              error: () => this.setIsLoadingMoreProducts(false),
            }),
          ),
      ),
    ),
  );

  // UPDATERS ----------------------------------------------------------------------------------------------------------
  readonly setIsLoadingProducts = this.updater(
    (state, isLoadingProducts: boolean): ProductMatchItemListState => ({
      ...state,
      loadingActions: {
        ...state.loadingActions,
        isLoadingProducts,
      },
    }),
  );

  readonly setIsLoadingMoreProducts = this.updater(
    (state, isLoadingMoreProducts: boolean): ProductMatchItemListState => ({
      ...state,
      loadingActions: {
        ...state.loadingActions,
        isLoadingMoreProducts,
      },
    }),
  );

  readonly setProducts = this.updater<HTTP_CP_MatchMortgageProducts_Response>(
    (state, response): ProductMatchItemListState => ({
      ...state,
      products: response.mortgageProducts ?? [],
      pagination: response.pagination ?? initialProductMatchItemListState.pagination,
      loadingActions: {
        isLoadingProducts: false,
        isLoadingMoreProducts: false,
      },
    }),
  );

  readonly setMortgagePartInfo = this.updater<{
    productMatchingInfo: MortgageFormProductMatchingInfoModel | undefined;
    mortgagePart: MortgagePartFormValues | undefined;
  }>(
    (state, mortgagePartInfo): ProductMatchItemListState => ({
      ...state,
      productMatchingInfo: mortgagePartInfo.productMatchingInfo ?? null,
      mortgagePart: mortgagePartInfo.mortgagePart ?? null,
    }),
  );

  readonly setPagination = this.updater((state, pagination: HTTP_Pagination_Response) => ({
    ...state,
    pagination,
  }));

  private readonly incrementPagination = this.updater((state) => ({
    ...state,
    pagination: {
      ...state.pagination,
      pageNumber: state.pagination.pageNumber + 1,
    },
  }));
}
